import _ from "lodash";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../redux/hooks";
import { useSelector } from "react-redux";
import { IRootState } from "../../../types/redux/IRootStore";
import React, { useEffect } from "react";

import { reprintPallets } from "../../../aplApi/reprintHelper";
import { ReprintParams } from "../../../types/reprintParams";
import { bootboxAlert } from "../bootboxWrapper";
import { getCombinedPdfReprint } from "../../../aplApi/pdfHelper";
import { getFirstLine } from "../../../aplUtils/stringUtills";
import DownloadButtonReprint from "./DownloadButtonReprint";
import AplSettingsSelector from "../../../redux/helpers/AplSettingsSelector";
import { closeModalRedux as closeModal } from "../../../redux/slices/modalPallets2.feature";
import { translateString } from "../../../translations/gridTranslationHelper";
import {
    setReprintCombinedPdf,
    setReprinted,
    setReprintingProgress,
    setReprintCopies,
    setReprintPrinterId,
    setReprintLabelId,
    clearReprint,
    addReprinted
} from "../../../redux/slices/reprint.feature";
import ModalDefects2PalletsDisplay from "./ModalDefects2PalletsDisplay";

export default function ModalReprint2() {
    const { t } = useTranslation();
    const aplSettings = AplSettingsSelector();
    const modalPallets2Redux = useSelector((state: IRootState) => state.modalPallets2);
    const reprintRedux = useSelector((state: IRootState) => state.reprint);
    const dispatch = useAppDispatch();
    const cancelRef = React.useRef(false);

    const executePrinting = async (reprintType: "label" | "defect" | "labelAndDefect" | "return") => {
        cancelRef.current = false;
        dispatch(setReprinted([]) as any);
        dispatch(setReprintCombinedPdf(""));
        for (let i: number = 0; i < modalPallets2Redux.pallets.length; i++) {
            if (cancelRef.current) {
                console.log(`${new Date().toISOString()} loop has terminated`);
                break;
            }
            dispatch(setReprintingProgress(i + 1));

            try {
                let reprintParams: ReprintParams = {
                    palletId: modalPallets2Redux.pallets[i].palletId,
                    printerId:
                        reprintRedux.selectedReprintPrinterId !== 0
                            ? reprintRedux.selectedReprintPrinterId
                            : reprintRedux.reprintPrinters[0].printerId,
                    copies: reprintRedux.reprintCopies,
                    labelId: reprintRedux.selectedReprintLabelId,
                    reprintLabel: reprintType === "label" || reprintType === "labelAndDefect",
                    reprintDefect: reprintType === "labelAndDefect" || reprintType === "defect",
                    palletReturn: reprintType === "return"
                };

                let results = await reprintPallets(reprintParams);
                let resultError = results.data.find(d => !d.success);
                dispatch(addReprinted(results.data) as any);
                if (resultError) {
                    bootboxAlert(`${t("main.error")} ${resultError.errorMessage}`);
                    dispatch(setReprintingProgress(0) as any);
                    return;
                }
            } catch (exception: any) {
                if (exception.response?.data) {
                    console.log(`${new Date().toISOString()} printing failed`, exception.response.data);
                    bootboxAlert(`${t("main.error")} ${getFirstLine(exception.response.data)}`);
                } else {
                    console.log(`${new Date().toISOString()} printing failed`, exception);
                    bootboxAlert(`${t("main.error")} ${exception.message}`);
                }

                dispatch(setReprintingProgress(0) as any);
                return;
            }
        }

        dispatch(setReprintingProgress(0));
    };

    const cancelPrinting = () => {
        cancelRef.current = true;
    };

    const getPrintButtons = (): JSX.Element => {
        if (reprintRedux.reprintingProgress > 0)
            return (
                <React.Fragment>
                    <div className="btn-group">
                        <button id="btnCancel" className="btn print-page-button nowrap btn-success" type="button" disabled={true}>
                            {`${reprintRedux.reprintingProgress} / ${modalPallets2Redux.pallets.length}`}
                            &nbsp; &nbsp;
                            <span
                                id="btnRefreshSpinner"
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden={true}
                            ></span>
                        </button>

                        <button id="btnCancel" className="btn btn-danger" type="button" onClick={cancelPrinting}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-x-circle"
                                viewBox="0 0 16 16"
                            >
                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                            </svg>
                        </button>
                    </div>
                </React.Fragment>
            );
        return (
            <React.Fragment>
                <Button
                    variant="secondary"
                    onClick={() => {
                        dispatch(clearReprint());
                        dispatch(closeModal());
                    }}
                >
                    {t("main.close")}
                </Button>
                {reprintRedux.reprintCombinedPdf ? <DownloadButtonReprint /> : null}

                <Button
                    className="btn btn-secondary"
                    onClick={() => {
                        executePrinting("defect");
                    }}
                >
                    {t("main.rxg_PrintDefect")}
                </Button>

                <Button
                    variant="primary"
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                        executePrinting("label");
                    }}
                >
                    {t("main.print")}
                </Button>
            </React.Fragment>
        );
    };

    useEffect(() => {
        if (reprintRedux.reprintingProgress === 0 && reprintRedux.reprinted.length > 0) {
            getCombinedPdfReprint(reprintRedux, dispatch, setReprintCombinedPdf);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reprintRedux.reprinted]);

    return (
        <React.Fragment>
            <div className="modal-header">
                <h5>{translateString("reprint", aplSettings.culture)}</h5>
                <button
                    type="button"
                    className="close"
                    onClick={() => {
                        dispatch(clearReprint());
                        dispatch(closeModal());
                    }}
                >
                    <span aria-hidden="true" className="text-danger">
                        ×
                    </span>
                    <span className="sr-only">Close</span>
                </button>
            </div>
            <div className="modal-body">
                <div className="overflow-auto" style={{ maxHeight: "40rem" }} id="modal-overflow">
                    {modalPallets2Redux.message && (
                        <div className="alert alert-danger" role="alert">
                            {modalPallets2Redux.message}
                        </div>
                    )}
                    <form className="m-2">
                        <ModalDefects2PalletsDisplay pallets={modalPallets2Redux.pallets} siteId={aplSettings.siteId} />
                        <br />
                        <div className="form-row">
                            <label htmlFor="ddPrinterNameModal">{t("main.printer")}</label>
                            <select
                                className="custom-select"
                                id="ddPrinterNameModal"
                                required={true}
                                onChange={e => {
                                    dispatch(setReprintPrinterId(Number(e.currentTarget.value)));
                                }}
                                value={reprintRedux.selectedReprintPrinterId}
                            >
                                {reprintRedux.reprintPrinters.map(x => {
                                    return (
                                        <option key={x.printerId} value={x.printerId}>
                                            {x.printerName}
                                        </option>
                                    );
                                })}
                            </select>
                            <label htmlFor="txtReprintCopies">{t("main.numberOfCopies")}</label>
                            <select
                                className="custom-select"
                                id="txtReprintCopies"
                                required={true}
                                onChange={e => {
                                    dispatch(setReprintCopies(Number(e.currentTarget.value)));
                                }}
                                value={reprintRedux.reprintCopies}
                            >
                                <option value="0">{t("main.workOrder")}</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                            </select>

                            {!aplSettings.isUsSite && (
                                <React.Fragment>
                                    <label htmlFor="txtReprintLabel">{t("main.litPalLabel")}</label>
                                    <select
                                        className="custom-select"
                                        id="txtReprintLabel"
                                        required={true}
                                        onChange={e => {
                                            dispatch(setReprintLabelId(Number(e.currentTarget.value)));
                                        }}
                                        value={reprintRedux.selectedReprintLabelId}
                                    >
                                        <option value={0}>{t("main.workOrder")}</option>
                                        {_.sortBy(reprintRedux.reprintLabels, x => x.labelName.toLowerCase()).map(x => {
                                            return (
                                                <option key={x.labelId} value={x.labelId}>
                                                    {x.labelName}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </React.Fragment>
                            )}
                        </div>
                    </form>
                </div>
            </div>
            <div className="modal-footer">{getPrintButtons()}</div>
        </React.Fragment>
    );
}
